import { usePeriod } from "../lib/PeriodContext";
import { useAuth } from "../lib/AuthContext";
import { LogOut, ChevronDown, Calendar } from "lucide-react";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { ProjectFilter } from "./ProjectFilter";

const PERIODS = [
  { key: "MTD", label: "MTD" },
  { key: "QTD", label: "QTD" },
  { key: "YTD", label: "YTD" },
  { key: "AS_ON", label: "As On" },
];

// Routes that NEED period selector
const PERIOD_ROUTES = ["/", "/sales", "/collection", "/targets", "/strategic"];
// Routes that NEED project filter
const PROJECT_ROUTES = ["/", "/sales", "/collection", "/targets", "/inventory", "/blocked", "/engineering", "/strategic"];
// Routes that NEED month selector
const MONTH_ROUTES = ["/", "/sales", "/collection", "/targets", "/strategic"];

export const Header = ({ title, subtitle }) => {
  const { months, asOf, setAsOf, period, setPeriod } = usePeriod();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const showPeriod = PERIOD_ROUTES.includes(loc.pathname);
  const showProject = PROJECT_ROUTES.includes(loc.pathname);
  const showMonth = MONTH_ROUTES.includes(loc.pathname);
  const currentMonth = months.find((m) => m.value === asOf);

  return (
    <header className="vexen-header" data-testid="header">
      <div className="px-3 sm:px-6 py-2 sm:py-0 sm:h-14 flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap">
        <div className="min-w-0 flex-1 sm:flex-initial">
          <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono truncate">
            {subtitle || "Realty Intelligence"}
          </div>
          <h1 className="text-sm sm:text-lg font-bold tracking-tight font-display truncate text-slate-900">
            {title}
          </h1>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
          {/* Period selector — visible on all screens incl. mobile */}
          {showPeriod && (
            <div
              className="flex items-center bg-slate-100 rounded-md p-0.5 border border-slate-200"
              data-testid="period-selector"
            >
              {PERIODS.map((p) => {
                const active = period === p.key;
                return (
                  <button
                    key={p.key}
                    onClick={() => setPeriod(p.key)}
                    className={`px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-semibold rounded transition-all whitespace-nowrap ${
                      active
                        ? "text-slate-900 shadow-sm ring-1 ring-amber-500/70"
                        : "text-slate-500 hover:text-slate-900"
                    }`}
                    style={active ? { background: "#D4AF37", color: "#0F1D32" } : undefined}
                    data-testid={`period-${p.key.toLowerCase().replace("_", "-")}`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          )}
          {showProject && <ProjectFilter />}
          {/* Month selector */}
          {showMonth && (
            <div className="relative">
              <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-2 px-3 py-1.5 text-xs bg-white border border-slate-200 rounded-md hover:border-slate-300 font-mono"
                data-testid="month-selector"
              >
                <Calendar size={13} />
                <span className="tabular-nums">{currentMonth?.label || "—"}</span>
                <ChevronDown size={12} />
              </button>
              {open && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-40 max-h-80 overflow-y-auto scroll-thin min-w-[140px]">
                    {months.map((m) => (
                      <button
                        key={m.value}
                        onClick={() => { setAsOf(m.value); setOpen(false); }}
                        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 font-mono ${
                          m.value === asOf ? "bg-slate-100 font-semibold" : ""
                        }`}
                        data-testid={`month-option-${m.value}`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {/* User */}
          <div className="hidden md:flex items-center gap-2 pl-3 border-l border-slate-200">
            <div className="text-right leading-tight">
              <div className="text-xs font-semibold text-slate-900">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{user?.role}</div>
            </div>
            <button
              onClick={logout}
              className="p-1.5 text-slate-500 hover:text-slate-900"
              data-testid="logout-button"
              title="Logout"
            >
              <LogOut size={15} />
            </button>
          </div>
          {/* Mobile logout */}
          <button
            onClick={logout}
            className="md:hidden p-1.5 text-slate-500 hover:text-slate-900 border border-slate-200 rounded-md"
            data-testid="logout-button-mobile"
            title="Logout"
          >
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </header>
  );
};
